import { useEffect, useMemo, useRef, useState } from "react";
import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { toLocalInputValue } from "../utils/reportDateFormat";
import { buildMonthGrid, WEEKDAYS } from "../utils/calendarGrid";

interface ReportDateRangePickerProps {
  startValue: string;
  endValue: string;
  onChangeStart: (value: string) => void;
  onChangeEnd: (value: string) => void;
  startError?: string;
  endError?: string;
}

const pad = (n: number) => String(n).padStart(2, "0");

const parseInputValue = (v: string): Date | null => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const stripTime = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const withTime = (day: Date, hh: number, mm: number) => {
  const d = new Date(day);
  d.setHours(hh, mm, 0, 0);
  return d;
};

const fmtShort = (d: Date | null) =>
  d ? d.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—";

const fmtTime = (d: Date | null) =>
  d ? d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }) : "";

export default function ReportDateRangePicker({
  startValue,
  endValue,
  onChangeStart,
  onChangeEnd,
  startError,
  endError,
}: ReportDateRangePickerProps) {
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const cellHover = isDarkMode ? "hover:bg-dark-hover" : "hover:bg-gray-100";
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const startDate = parseInputValue(startValue);
  const endDate = parseInputValue(endValue);

  const [viewMonth, setViewMonth] = useState(() => startDate ?? new Date());

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const applyDay = (day: Date) => {
    if (!startDate || (startDate && endDate)) {
      onChangeStart(toLocalInputValue(withTime(day, startDate?.getHours() ?? 0, startDate?.getMinutes() ?? 0)));
      onChangeEnd("");
      return;
    }
    if (stripTime(day) < stripTime(startDate)) {
      onChangeStart(toLocalInputValue(withTime(day, startDate.getHours(), startDate.getMinutes())));
      return;
    }
    onChangeEnd(toLocalInputValue(withTime(day, endDate?.getHours() ?? 23, endDate?.getMinutes() ?? 59)));
  };

  const setStartTime = (time: string) => {
    if (!startDate) return;
    const [hh, mm] = time.split(":").map(Number);
    onChangeStart(toLocalInputValue(withTime(startDate, hh, mm)));
  };

  const setEndTime = (time: string) => {
    if (!endDate) return;
    const [hh, mm] = time.split(":").map(Number);
    onChangeEnd(toLocalInputValue(withTime(endDate, hh, mm)));
  };

  const applyPreset = (daysBack: number) => {
    const end = new Date();
    end.setHours(23, 59, 0, 0);
    const start = new Date();
    start.setDate(start.getDate() - daysBack);
    start.setHours(0, 0, 0, 0);
    onChangeStart(toLocalInputValue(start));
    onChangeEnd(toLocalInputValue(end));
    setViewMonth(start);
  };

  const isPresetActive = (daysBack: number) => {
    if (!startDate || !endDate) return false;
    const today = stripTime(new Date());
    const expectedStart = new Date(today);
    expectedStart.setDate(expectedStart.getDate() - daysBack);
    return sameDay(startDate, expectedStart) && sameDay(endDate, today);
  };

  const cells = useMemo(() => buildMonthGrid(viewMonth), [viewMonth]);
  const monthLabel = viewMonth.toLocaleDateString("es-MX", { month: "long", year: "numeric" });

  return (
    <div className="relative w-full lg:w-auto" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full lg:w-auto items-center gap-2 pl-2.5 pr-2 h-10 rounded-lg border-2 text-left min-w-[10.5rem] transition-colors ${themeClasses.inputBg} ${themeClasses.inputText} hover:border-primary-500/60 ${
          open ? "border-primary-500" : themeClasses.inputBorder
        }`}
      >
        <i className={`ri-calendar-line ${themeClasses.textSecondary} shrink-0`} />
        <div className="flex flex-col leading-tight gap-0.5 min-w-0 max-lg:flex-row max-lg:items-center max-lg:gap-1.5">
          <span className="text-[11px] max-lg:text-xs whitespace-nowrap">{fmtShort(startDate)} {fmtTime(startDate)}</span>
          <span className={`text-[11px] max-lg:text-xs whitespace-nowrap ${themeClasses.textSecondary}`}>→ {fmtShort(endDate)} {fmtTime(endDate)}</span>
        </div>
        <i className={`ri-arrow-down-s-line ${themeClasses.textSecondary} shrink-0 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {(startError || endError) && (
        <span className="text-red-500 text-xs block">{startError || endError}</span>
      )}

      {open && (
        <div
          className={`absolute z-30 mt-1 max-lg:left-1/2 max-lg:-translate-x-1/2 max-lg:max-w-[calc(100vw-2rem)] p-3 rounded-lg border shadow-lg ${themeClasses.cardBg} ${themeClasses.cardBorder}`}
          style={{ width: 260 }}
        >
          <div className="flex flex-wrap gap-1 mb-2">
            {[
              { label: "Hoy", days: 0 },
              { label: "7 días", days: 7 },
              { label: "30 días", days: 30 },
            ].map((preset) => {
              const active = isPresetActive(preset.days);
              return (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => applyPreset(preset.days)}
                  className={`text-[11px] px-2 py-0.5 rounded border transition-colors ${
                    active
                      ? `${themeClasses.buttonBg} text-white border-transparent`
                      : `${themeClasses.cardBorder} ${isDarkMode ? "text-white" : themeClasses.text} hover:border-primary-500 hover:text-primary-500`
                  }`}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between mb-1">
            <button
              type="button"
              onClick={() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
              title="Mes anterior"
              aria-label="Mes anterior"
              className={`p-1 ${themeClasses.text} hover:text-primary-500`}
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            <span className={`text-xs font-medium capitalize ${themeClasses.text}`}>{monthLabel}</span>
            <button
              type="button"
              onClick={() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
              title="Mes siguiente"
              aria-label="Mes siguiente"
              className={`p-1 ${themeClasses.text} hover:text-primary-500`}
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 text-center mb-1">
            {WEEKDAYS.map((w) => (
              <span key={w} className={`text-[10px] ${themeClasses.textSecondary}`}>{w}</span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((day, i) => {
              if (!day) return <span key={i} />;
              const isStart = !!startDate && sameDay(day, startDate);
              const isEnd = !!endDate && sameDay(day, endDate);
              const inRange = !!startDate && !!endDate && stripTime(day) > stripTime(startDate) && stripTime(day) < stripTime(endDate);
              const isEndpoint = isStart || isEnd;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => applyDay(day as Date)}
                  className={`text-[11px] h-6 w-6 rounded-full flex items-center justify-center ${
                    isEndpoint
                      ? `${themeClasses.buttonBg} text-white`
                      : inRange
                        ? `bg-primary-500/15 ${themeClasses.text}`
                        : `${themeClasses.text} ${cellHover}`
                  }`}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2 mt-2">
            <div className="flex-1">
              <label className={`text-[10px] ${themeClasses.textSecondary}`}>Hora inicio</label>
              <input
                type="time"
                disabled={!startDate}
                value={startDate ? `${pad(startDate.getHours())}:${pad(startDate.getMinutes())}` : ""}
                onChange={(e) => setStartTime(e.target.value)}
                className={`w-full p-1 rounded border text-xs disabled:opacity-40 ${themeClasses.inputBg} ${themeClasses.inputBorder} ${themeClasses.inputText}`}
              />
            </div>
            <div className="flex-1">
              <label className={`text-[10px] ${themeClasses.textSecondary}`}>Hora fin</label>
              <input
                type="time"
                disabled={!endDate}
                value={endDate ? `${pad(endDate.getHours())}:${pad(endDate.getMinutes())}` : ""}
                onChange={(e) => setEndTime(e.target.value)}
                className={`w-full p-1 rounded border text-xs disabled:opacity-40 ${themeClasses.inputBg} ${themeClasses.inputBorder} ${themeClasses.inputText}`}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => setOpen(false)}
            className={`mt-2 w-full text-xs px-2 py-1 rounded-lg ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2}`}
          >
            Listo
          </button>
        </div>
      )}
    </div>
  );
}
